import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation, useTranslation } from 'react-i18next';
import { BiDownload, BiX } from 'react-icons/bi';
import { BsTable } from 'react-icons/bs';
import { Modal, Button, Table } from 'react-bootstrap';
import Utils from '../../../core/utilities/Utils';
import * as XLSX from 'xlsx';
import './TableViewModal.scss';

const TableViewModal = ({ t, open, onClose, chartData, chartConfig, title, year }) => {
  const { i18n } = useTranslation();

  if (!open) return null;

  // Function to get localized label (following useBasicGraphCard pattern)
  const getLocalizedLabel = (localizedContents, languageCode, fieldName) => {
    if (!Array.isArray(localizedContents)) return null;
    const localized = localizedContents.find(
      (content) => content.languageCode === languageCode && content.fieldName === fieldName
    );
    return localized ? localized.translatedValue : null;
  };

  // Function to render tabular data based on chart type
  const renderTabularData = () => {
    if (!chartData) {
      return (
        <div className="table-view-modal__no-data">
          <p>{t("TableViewModal.noData.label")}</p>
        </div>
      );
    }

    const chartType = chartConfig?.type || 'BAR';

    if (chartType === 'PIE' || chartType === 'DOUGHNUT') {
      return renderPieChartTable();
    } else if (chartType === 'KPI') {
      return renderKPIChartTable();
    } else {
      return renderStandardChartTable();
    }
  };

  // Render table for pie/doughnut charts
  const renderPieChartTable = () => {
    const datasets = chartData.datasets || [];
    const labels = chartData.labels || [];

    // Handle localized labels for pie charts
    const localizedLabels = labels.map((label, index) => {
      if (typeof label === 'object' && label.localizedContents) {
        return getLocalizedLabel(label.localizedContents, i18n.language, 'label') || label.label || label;
      }
      return label;
    });

    return (
      <Table bordered hover className="m-0 rounded">
        <thead>
          <tr>
            <th>{t("TableViewModal.category.label")}</th>
            <th>{t("TableViewModal.value.label")}</th>
            <th>{t("TableViewModal.percentage.label")}</th>
          </tr>
        </thead>
        <tbody>
          {localizedLabels.map((label, index) => {
            const value = datasets[0]?.data?.[index] || 0;
            const total = datasets[0]?.data?.reduce((sum, val) => sum + val, 0) || 1;
            const percentage = ((value / total) * 100).toFixed(1);

            return (
              <tr key={`pie-row-${index}`}>
                <td>{label}</td>
                <td>{Utils.formattedNumber(value)}</td>
                <td>{percentage}%</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  // Render table for KPI charts
  const renderKPIChartTable = () => {
    const datasets = chartData.datasets || [];
    const labels = chartData.labels || [];

    // Handle localized labels for KPI charts
    const localizedLabels = labels.map((label, index) => {
      if (typeof label === 'object' && label.localizedContents) {
        return getLocalizedLabel(label.localizedContents, i18n.language, 'label') || label.label || label;
      }
      return label;
    });

    return (
      <Table bordered hover className="m-0 rounded">
        <thead>
          <tr>
            <th>{t("TableViewModal.metric.label")}</th>
            <th>{t("TableViewModal.value.label")}</th>
          </tr>
        </thead>
        <tbody>
          {localizedLabels.map((label, index) => {
            const value = datasets[0]?.data?.[index] || 0;

            return (
              <tr key={`kpi-row-${index}`}>
                <td>{label}</td>
                <td>{Utils.formattedNumber(value)}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  // Render table for standard charts (line, bar, etc.)
  const renderStandardChartTable = () => {
    const datasets = chartData.datasets || [];
    console.log('datasets', datasets);
    const labels = chartData.labels || [];

    if (!labels.length || !datasets.length) {
      return (
        <div className="table-view-modal__no-data">
          <p>{t("TableViewModal.noData.label")}</p>
        </div>
      );
    }

    // Handle localized labels for standard charts
    const localizedLabels = labels.map((label, labelIndex) => {
      if (typeof label === 'object' && label.localizedContents) {
        return getLocalizedLabel(label.localizedContents, i18n.language, 'label') || label.label || label;
      }
      return label;
    });

    // Handle localized dataset labels
    const localizedDatasets = datasets.map((dataset, index) => {
      if (dataset.label && typeof dataset.label === 'object' && dataset.label.localizedContents) {
        return {
          ...dataset,
          localizedLabel: getLocalizedLabel(dataset.label.localizedContents, i18n.language, 'label') || dataset.label.label || dataset.label
        };
      }
      return {
        ...dataset,
        localizedLabel: dataset.label || t("TableViewModal.series.label", { number: index + 1 })
      };
    });

    return (
      <Table bordered hover className="m-0 rounded">
        <thead>
          <tr>
            <th>{t("TableViewModal.period.label")}</th>
            {localizedDatasets.map((dataset, index) => (
              <th key={`header-${index}`}>{dataset.localizedLabel}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {localizedLabels.map((label, labelIndex) => (
            <tr key={`row-${labelIndex}`}>
              <td>{label}</td>
              {localizedDatasets.map((dataset, datasetIndex) => {
                const value = dataset.data?.[labelIndex] || 0;
                return (
                  <td key={`cell-${labelIndex}-${datasetIndex}`}>
                    {Utils.formattedNumber(value)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  // Handle download functionality
  const handleDownload = () => {
    if (!chartData) return;

    const chartType = chartConfig?.type || 'BAR';
    let fileName = title || 'TableData';
    let headers = [];
    let rows = [];

    if (chartType === 'PIE' || chartType === 'DOUGHNUT') {
      // Export pie chart data
      const datasets = chartData.datasets || [];
      const labels = chartData.labels || [];

      headers = [t("TableViewModal.category.label"), t("TableViewModal.value.label"), t("TableViewModal.percentage.label")];

      rows = labels.map((label, index) => {
        const value = datasets[0]?.data?.[index] || 0;
        const total = datasets[0]?.data?.reduce((sum, val) => sum + val, 0) || 1;
        const percentage = ((value / total) * 100).toFixed(1);

        // Handle localized label for export
        const localizedLabel = typeof label === 'object' && label.localizedContents
          ? getLocalizedLabel(label.localizedContents, i18n.language, 'label') || label.label || label
          : label;

        return [localizedLabel, value, `${percentage}%`];
      });

    } else if (chartType === 'KPI') {
      // Export KPI data
      const datasets = chartData.datasets || [];
      const labels = chartData.labels || [];

      headers = [t("TableViewModal.metric.label"), t("TableViewModal.value.label")];

      rows = labels.map((label, index) => {
        const value = datasets[0]?.data?.[index] || 0;

        // Handle localized label for export
        const localizedLabel = typeof label === 'object' && label.localizedContents
          ? getLocalizedLabel(label.localizedContents, i18n.language, 'label') || label.label || label
          : label;

        return [localizedLabel, value];
      });

    } else {
      // Export standard chart data (line, bar, etc.)
      const datasets = chartData.datasets || [];
      const labels = chartData.labels || [];

      if (!labels.length || !datasets.length) return;

      // Handle localized dataset labels for headers
      const localizedDatasetLabels = datasets.map((dataset, index) => {
        if (dataset.label && typeof dataset.label === 'object' && dataset.label.localizedContents) {
          return getLocalizedLabel(dataset.label.localizedContents, i18n.language, 'label') || dataset.label.label || dataset.label;
        }
        return dataset.label || t("TableViewModal.series.label", { number: index + 1 });
      });

      headers = [t("TableViewModal.period.label"), ...localizedDatasetLabels];

      rows = labels.map((label, labelIndex) => {
        // Handle localized label for export
        const localizedLabel = typeof label === 'object' && label.localizedContents
          ? getLocalizedLabel(label.localizedContents, i18n.language, 'label') || label.label || label
          : label;

        const row = [localizedLabel];
        datasets.forEach((dataset) => {
          const value = dataset.data?.[labelIndex] || 0;
          row.push(value);
        });
        return row;
      });
    }

    // Create Excel file
    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

    // Download the file
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  return (
    <Modal show={open} onHide={onClose} size="lg" centered className="NewDashboard_modal">
      <Modal.Header closeButton>
        <Modal.Title as="h5">{title || t("TableViewModal.title.label")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='p-4'>
          {renderTabularData()}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t("TableViewModal.close.label")}
        </Button>
        <Button variant="primary" onClick={handleDownload}>
          {t("TableViewModal.downloadSpreadsheet.label")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

TableViewModal.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  chartData: PropTypes.object,
  chartConfig: PropTypes.object,
  title: PropTypes.string,
  year: PropTypes.number
};

export default withTranslation()(TableViewModal); 